import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  setAppId,
  selectedApplicationData,
  selectedApplicationIdData,
  addBreadcrumb,
} from "../../store/ApplicationSlice";
import axios from "axios";
import MenuList from "../../components/MenuList";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PaginationControls from "./Pagination";
import useApplicationData from "../../hooks/useApplicationData";

// Utility functions
const truncateString = (str = "", limit = 50) =>
  str.length > limit ? str.slice(0, limit - 3) + "..." : str;

const getPriorityClass = (priority) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "bg-orange-600 text-white";
    case "medium":
      return "medium-priority";
    case "low":
      return "low-priority";
    default:
      return "";
  }
};

// get the subtracted days
function subDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() - days); // Subtract days
  return result;
}

const IntakeInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchApplicationData } = useApplicationData();
  const appetiteData = useSelector((state) => state.intakeData.appetiteData);
  const selectedApplicationId = useSelector(
    (state) => state.intakeData.selectedAppId
  );
  const totalApplications = useSelector(
    (state) => state.intakeData.totalApplications
  );

  const accessToken = localStorage.getItem("access_token");
  const refreshToken = localStorage.getItem("refresh_token");

  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedRow, setHighlightedRow] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: "asc",
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // State for loader

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15; // Number of items per page

  const [filters, setFilters] = useState({
    filterBy: "last_30_days",
    startDate: "", // Set initial value
    endDate: "",
  });

  // Dropdown filter options
  const filterOptions = [
    { label: "Last 3 days", value: "last_3_days" },
    { label: "Last 7 days", value: "last_7_days" },
    { label: "Last 30 days", value: "last_30_days" },
    { label: "Custom", value: "custom" },
  ];

  // Handle Filter Change
  const handleFilterChange = (event) => {
    const value = event.target.value;

    const dateRanges = {
      last_3_days: subDays(new Date(), 3),
      last_7_days: subDays(new Date(), 7),
      last_30_days: subDays(new Date(), 30),
    };

    setFilters({
      filterBy: value,
      startDate: dateRanges[value] || null, // Use predefined range or null for "custom"
      endDate: value === "custom" ? null : new Date(), // Null if "custom", else today
    });
  };

  // Build table data
  const tableData = useMemo(() => {
    return (
      appetiteData?.map((app) => ({
        name: truncateString(app.applicant_info?.name),
        sku: app.lob,
        agent: app.agent_name,
        applicationClassification: app.application_classification,
        status: app.application_status,
        priority: app.priority,
        date: app.application_last_modified,
        id: app.application_id,
      })) || []
    );
  }, [appetiteData]);

  useEffect(() => {
    if (tableData.length) {
      dispatch(setAppId(tableData[0].id));
    }
  }, [tableData, dispatch]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig.column) return tableData;
    return [...tableData].sort((a, b) => {
      if (a[sortConfig.column] < b[sortConfig.column])
        return sortConfig.direction === "asc" ? -1 : 1;
      if (a[sortConfig.column] > b[sortConfig.column])
        return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [tableData, sortConfig]);

  // Search logic
  const filteredData = useMemo(() => {
    return sortedData.filter((row) => {
      const rowDate = new Date(row.date);
      const { startDate, endDate } = filters;

      // Handle filtering logic
      if (startDate && rowDate < startDate) return false;
      if (endDate && rowDate > endDate) return false;

      // Apply text search filter
      if (!searchTerm) return true;
      return Object.values(row).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [searchTerm, sortedData, filters]);

  // Pagination Logic: Get items for the current page
  const paginatedData = useMemo(() => {
    return filteredData.slice(0, itemsPerPage);
  }, [filteredData, itemsPerPage]);

  // Handle Pagination
  const totalPages = Math.ceil(totalApplications / itemsPerPage);

  // Fetch application data on selectedApplicationId change
  useEffect(() => {
    if (!selectedApplicationId) return;

    const fetchApplicationData = async () => {
      try {
        const response = await axios.post(
          `https://insureai-intake-dev-1074099157700.us-central1.run.app/insureai/ml/v1/no_app/enquiry?app_id=${selectedApplicationId}&token=25d8feda-c5be-40da-b0b5-828c23fdb7a0`,
          null,
          { headers: { "Content-Type": "application/json" } }
        );
        dispatch(selectedApplicationIdData(response.data));
      } catch (error) {
        console.error("Error fetching selected id application data:", error);
      }
    };

    fetchApplicationData();
  }, [selectedApplicationId, dispatch]);

  // Event handlers
  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleRowClick = (index, id) => {
    setHighlightedRow(index);
    dispatch(setAppId(id));
    dispatch(
      selectedApplicationData(
        appetiteData.find((app) => app.application_id === id)
      )
    );
  };

  const handleRowDoubleClick = (id) => {
    const route = `/app/intake/${id}`;
    if (location.pathname !== route) {
      dispatch(addBreadcrumb({ label: "Application Overview", path: route }));
    }
    navigate(route);
  };

  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  // Handle Pagination
  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    setIsLoading(true); // Show loader when API call starts
    await fetchApplicationData(
      accessToken,
      refreshToken,
      newPage,
      itemsPerPage
    );
    setIsLoading(false); // Hide loader after API call completes
  };
  
  // Truncate SKU
  const truncateSku = (sku) => {
    if (!sku) return "";
    const parts = sku.split(",");
    if (parts.length > 2) {
      return `${parts.slice(0, 2).join(", ")}...`;
    }
    return sku; // If it's already 2 or fewer items, return as is
  };

  return (
    <div className="mx-auto relative">
      {/* Loader Overlay */}
      {isLoading && location.pathname !== "/" && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.4)", // Transparent grey background
            zIndex: 9999, // Ensures it overlays everything
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress
            size={60}
            sx={{
              color: "#ea580c",
            }}
          />
        </Box>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        {/* Left Side: Menu and Title */}
        <div className="flex items-center space-x-4">
          <MenuList isOpen={isMenuOpen} toggleMenu={toggleMenu} />
          <h1 className="font-semibold py-2 text-2xl text-orange-600">
            Intake Triage and Priority
          </h1>
        </div>

        {/* Right Side: Filters */}
        <div className="flex items-center space-x-4">
          {/* Filter Dropdown */}
          <Select
            value={filters.filterBy}
            onChange={handleFilterChange}
            size="small"
            style={{ minWidth: 150 }}
          >
            {filterOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>

          {/* Date Filters (conditionally shown if "Custom Date Range" is selected) */}
          {filters.filterBy === "custom" && (
            <div className="flex items-center space-x-4">
              {/* Start Date Input */}
              <input
                type="date"
                value={
                  filters.startDate
                    ? filters.startDate.toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    startDate: new Date(e.target.value),
                  }))
                }
                className="p-2 rounded border border-gray-300"
              />

              {/* End Date Input */}
              <input
                type="date"
                value={
                  filters.endDate
                    ? filters.endDate.toISOString().split("T")[0]
                    : ""
                }
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    endDate: new Date(e.target.value),
                  }))
                }
                className="p-2 rounded border border-gray-300"
              />
            </div>
          )}
        </div>
      </div>

      {/* Table Wrapper */}
      <div
        className={`flex-grow transform transition-transform duration-300  intake-table-wrapper`}
      >
        <div className="grid grid-cols-16 gap-4">
          <div
            className="overflow-x-auto intake-table-data"
            style={{ maxHeight: "474px" }}
          >
            <table className="min-w-full bg-white">
              <thead className="bg-gray-200 table-header">
                <tr>
                  {[
                    { key: "name", label: "Applicant Name" },
                    { key: "sku", label: "Line Of Business" },
                    { key: "agent", label: "Agent" },
                    {
                      key: "applicationClassification",
                      label: "Classification",
                    },
                    { key: "status", label: "Status" },
                    { key: "priority", label: "Priority" },
                    { key: "date", label: "Date" },
                    { key: "id", label: "Application ID" },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      className="text-left py-3 px-4 cursor-pointer"
                      onClick={() => handleSort(key)}
                    >
                      {label}
                      {sortConfig.column === key &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUpwardIcon />
                        ) : (
                          <ArrowDownwardIcon />
                        ))}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th colSpan="8" className="px-2 py-3">
                    <div className="relative">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search by text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full p-2 pl-10 rounded bg-gray-200"
                      />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`cursor-pointer ${
                      highlightedRow === index ? "active-row" : ""
                    }`}
                    onClick={() => handleRowClick(index, row.id)}
                    onDoubleClick={() => handleRowDoubleClick(row.id)}
                  >
                    {Object.keys(row).map((key) => (
                      <td key={key} className="border px-4 py-4 capitalize">
                        {key === "priority" ? (
                          <span
                            className={`${getPriorityClass(
                              row[key]
                            )} label-priority`}
                          >
                            {row[key]}
                          </span>
                        ) : key === "date" ? (
                          new Date(row[key]).toLocaleDateString("en-GB") // Format date
                        ) : key === "sku" ? (
                          // ✅ MUI Tooltip for better hover effect
                          <Tooltip title={row[key]} arrow>
                            <span className="cursor-pointer">
                              {truncateSku(row[key])}
                            </span>
                          </Tooltip>
                        ) : (
                          row[key]
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default IntakeInfo;
